import * as React from 'react';

import { enumQuoteStatus, Unit } from '@rematter/frontend-lib-api-client';
import { Button, cn, ContentCard, Footer } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

import { QuoteLineItemsTable } from '../_shared/LineItems';
import { QuoteLineItemsSummaryFooter } from '../_shared/LineItems/QuoteLineItemsSummaryFooter';

import {
  QuoteLineItemUpdateInput,
  quoteUpdateAtom,
} from '~/modules/brokerage/_graphql/mutationQuoteUpdate';
import {
  $quoteDraftLineItems,
  $quoteEffectiveLineItems,
} from '~/modules/brokerage/_state/quoteDetail.state';
import {
  QUOTE_TEMP_ID_PREFIX,
  useQuoteLineItemsManager,
} from '~/modules/brokerage/hooks/useQuoteLineItemsManager';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { QuoteDetailRecord } from '~/modules/brokerage/types/quote';

type Props = {
  quote?: QuoteDetailRecord | null;
  onRefetch?: () => void;
};

export const QuoteTabMaterials: React.FC<Props> = ({ quote, onRefetch }) => {
  const t = useTranslations();
  const store = useStore();

  const [isSaving, setIsSaving] = React.useState(false);

  const effectiveLineItems = useAtomValue($quoteEffectiveLineItems);

  const setDraftLineItems = useSetAtom($quoteDraftLineItems);
  const updateQuote = useSetAtom(quoteUpdateAtom({}));

  const toast = useToast();
  const { addLineItem, updateLineItem, deleteLineItem } =
    useQuoteLineItemsManager({
      lineItems: effectiveLineItems,
      onLineItemsChange: setDraftLineItems,
    });

  const canEdit =
    quote?.status === enumQuoteStatus.Draft ||
    quote?.status === enumQuoteStatus.Sent;

  const handleDiscard = React.useCallback(() => {
    setDraftLineItems(null);
    toast.success(t.quotes.toast.discarded);
  }, [setDraftLineItems, toast, t]);

  const handleSave = React.useCallback(async () => {
    if (!quote?.id) return;

    const currentItems = store.get($quoteEffectiveLineItems);
    const validItems = currentItems.filter((li) => li.itemId);

    if (validItems.length === 0 && currentItems.length > 0) {
      toast.error(t.quotes.toast.materialRequired);
      return;
    }

    try {
      setIsSaving(true);

      const lineItemsInput: QuoteLineItemUpdateInput[] = validItems.map(
        (li) => ({
          id: li.id.startsWith(QUOTE_TEMP_ID_PREFIX) ? undefined : li.id,
          itemId: li.itemId,
          quantity: li.quantity,
          unit: li.unit as Unit,
          unitPrice: li.unitPrice,
          notes: li.notes,
        }),
      );

      await updateQuote({
        id: quote.id,
        lineItems: lineItemsInput,
      });

      toast.success(t.quotes.toast.updated);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.quotes.toast.updateError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }, [quote?.id, store, updateQuote, toast, t, onRefetch]);

  React.useEffect(() => {
    if (quote?.timestamps?.updatedAt) {
      setDraftLineItems(null);
    }
  }, [quote?.timestamps?.updatedAt, setDraftLineItems]);

  if (!quote) return null;

  return (
    <div
      className={cn(
        '@QuoteTabMaterials',
        'flex h-full flex-col overflow-hidden p-5',
      )}
    >
      <ContentCard className="flex flex-col overflow-hidden rounded-lg p-0">
        <div className="flex-1 overflow-auto px-5 py-6 pr-4">
          <QuoteLineItemsTable
            lineItems={effectiveLineItems}
            onUpdateLineItem={updateLineItem}
            onAddLineItem={addLineItem}
            onDeleteLineItem={deleteLineItem}
            disabled={!canEdit}
          />
        </div>

        <Footer className="@QuoteLineItemsFooter flex-shrink-0" displayShadow>
          <div className="flex items-center justify-end gap-5 px-5 py-2.5">
            <QuoteLineItemsSummaryFooter lineItems={effectiveLineItems} />

            {canEdit && (
              <div className="ml-4 flex items-center justify-end gap-5">
                <Button
                  color="neutral"
                  variant="outline"
                  onPress={handleDiscard}
                  isDisabled={isSaving}
                >
                  {t.quotes.detail.footer.discard}
                </Button>
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleSave}
                  isLoading={isSaving}
                >
                  {t.quotes.detail.footer.saveUpdates}
                </Button>
              </div>
            )}
          </div>
        </Footer>
      </ContentCard>
    </div>
  );
};
